import React, { ReactNode } from "react";

import { ExternalLink, GitBranch, UserCheck } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { contentTypeLabel } from "@/lib/tina/content-types";
import { HitlReasonBadge } from "@/pages/tina/components/hitl-reason-badge";
import { ItemStatusBadge } from "@/pages/tina/components/item-status-badge";
import { ResolvedByChip } from "@/pages/tina/components/resolved-by-chip";
import { StageTimeline } from "@/pages/tina/items/components/stage-timeline";
import {
  itemLineageLabel,
  itemSourceUrl,
  shortId,
} from "@/pages/tina/items/item-context";
import type { PipelineItem, StageExecutionRead } from "@/types/tina";

interface Props {
  item: PipelineItem;
  stages: StageExecutionRead[];
  main: ReactNode;
}

export function ItemsShowScaffold({ item, stages, main }: Props) {
  const sourceUrl = itemSourceUrl(item);
  const lineage = itemLineageLabel(item);
  const metadataJson = React.useMemo(
    () => (item.metadata ? JSON.stringify(item.metadata, null, 2) : null),
    [item.metadata],
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold font-mono tracking-tight">
            Item {item.id.slice(0, 8)}
          </h1>
          <CopyButton text={item.id} message="Item ID copied" />
          <ItemStatusBadge status={item.status} />
          {item.hitl_reason && <HitlReasonBadge reason={item.hitl_reason} />}
          {/* Beside the reason badge, because they answer the same question
              from two sides: what parked it, and who cleared it. Absent on an
              item whose park predates migration 0027 - `hitl_resolved_by` was
              never backfilled, so no name exists to show. See
              `resolved-by-chip.tsx` for why an absent name is not a "no". */}
          {item.hitl_resolved_by && (
            <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-muted-foreground">
              <UserCheck className="h-3.5 w-3.5 shrink-0" />
              resolved by
              <ResolvedByChip
                uid={item.hitl_resolved_by}
                resolvedAt={item.hitl_resolved_at}
              />
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground tabular-nums">
          <span>
            run{" "}
            <span className="font-mono text-foreground">
              {item.run_id.slice(0, 8)}
            </span>
          </span>
          <span className="text-muted-foreground/50">·</span>
          <span>
            site <span className="text-foreground">{item.site}</span>
          </span>
          <span className="text-muted-foreground/50">·</span>
          <span>
            type{" "}
            {/* The label, with the wire value on hover. A curator on an
                enrichment job used to read `enrichment_show_genre` here — the
                raw `content_type` on the one header this slice exists to fix. */}
            <span className="text-foreground" title={item.content_type}>
              {contentTypeLabel(item.content_type)}
            </span>
          </span>
        </div>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-xs text-blue-600 hover:underline break-all"
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[60ch]">{sourceUrl}</span>
          </a>
        )}
        {(lineage || item.group_id || item.parent_event_id) && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <GitBranch className="h-3.5 w-3.5" />
            {lineage && <Badge variant="secondary">{lineage}</Badge>}
            {item.group_id && (
              <span>
                group{" "}
                <span className="font-mono text-foreground">
                  {shortId(item.group_id)}
                </span>
              </span>
            )}
            {item.parent_event_id && (
              <a
                href={`/tina/items/show/${item.parent_event_id}`}
                className="font-mono text-blue-600 hover:underline"
              >
                parent {shortId(item.parent_event_id)}
              </a>
            )}
            {item.depends_on_event_id &&
              item.depends_on_event_id !== item.parent_event_id && (
                <a
                  href={`/tina/items/show/${item.depends_on_event_id}`}
                  className="font-mono text-blue-600 hover:underline"
                >
                  depends {shortId(item.depends_on_event_id)}
                </a>
              )}
          </div>
        )}
      </header>

      <div className="grid grid-cols-[1fr_340px] gap-6 items-start">
        <div className="min-w-0">{main}</div>
        <aside className="flex flex-col gap-4 sticky top-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">
                Stage executions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StageTimeline executions={stages} />
            </CardContent>
          </Card>
          <Card>
            <Collapsible>
              <CollapsibleTrigger className="flex w-full items-center justify-between p-6 text-sm font-semibold hover:bg-accent/30 rounded-lg transition-colors">
                Metadata
                <span className="text-xs text-muted-foreground font-normal">
                  {Object.keys(item.metadata ?? {}).length} keys
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-6 pb-6">
                  <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
                    {metadataJson ?? "{}"}
                  </pre>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </aside>
      </div>
    </div>
  );
}
